import { useState } from "react";
import { Building2, Check, ChevronsUpDown, Plus } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ManageCompaniesDialog } from "./ManageCompaniesDialog";
import { CreateCompanyDialog } from "./CreateCompanyDialog";

export function CompanySwitcher() {
  const { selectedCompanyId, setSelectedCompanyId } = useCompany();
  const [open, setOpen] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);

  const { data: companies = [], refetch } = trpc.company.list.useQuery();

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  const handleCompanyCreated = (companyId: number) => {
    refetch();
    setSelectedCompanyId(companyId);
    setShowCreateDialog(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select a company"
            className="w-[250px] justify-between"
          >
            <Building2 className="mr-2 h-4 w-4" />
            <span className="truncate">
              {selectedCompany ? selectedCompany.name : "Select company..."}
            </span>
            <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0">
          <Command>
            <CommandInput placeholder="Search company..." />
            <CommandList>
              <CommandEmpty>No company found.</CommandEmpty>
              <CommandGroup heading="Your Companies">
                {companies.map(company => (
                  <CommandItem
                    key={company.id}
                    onSelect={() => {
                      setSelectedCompanyId(company.id);
                      setOpen(false);
                    }}
                    className="text-sm"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span className="truncate">{company.name}</span>
                    {company.id === selectedCompanyId && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    setShowCreateDialog(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Company
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    setShowManageDialog(true);
                  }}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  Manage Companies
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <CreateCompanyDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCompanyCreated={handleCompanyCreated}
      />

      <ManageCompaniesDialog
        open={showManageDialog}
        onOpenChange={setShowManageDialog}
      />
    </>
  );
}
